import { inject, Injectable } from '@angular/core';
import {
  AccountType,
  AssignedMicrosoft365License,
  LicensingState,
  Microsoft360View,
  Microsoft365LicensesFieldConfig,
  MicrosoftSecurityGroupOption,
  MicrosoftSecurityGroupsFieldConfig,
  MicrosoftSharedVoicemailGroupOption,
  MicrosoftSharedVoicemailGroupsFieldConfig,
  TileStatus,
  ZpmTileConfig,
} from '../../../shared/models/generated/ziroModels';
import { ServiceTileType } from '../../cisco/user-detail-home/service-tile-type.model';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { cloneDeep } from 'lodash';
import { MicrosoftTile, MicrosoftTileState } from './tiles.model';

@Injectable()
export class MicrosoftTileMappingService {
  private telephoneNumberFilter = inject(TelephoneNumberFilter);

  ziroIcons = ZiroIcons;
  microsoft360View: Microsoft360View;

  private _defaultTileState: MicrosoftTileState = {
    description: '',
    isAdding: false,
    isDeleting: false,
    isDisplayed: false,
    isEnabled: false,
  } as MicrosoftTileState;
  private isResourceAccount: boolean;

  mapServiceToTile(
    tile: MicrosoftTile,
    tileConfig: ZpmTileConfig,
    ms360View: Microsoft360View,
    isConfigured?: boolean,
    is365LicenseSyncInProgress?: boolean,
    fieldConfig?:
      | MicrosoftSecurityGroupsFieldConfig
      | Microsoft365LicensesFieldConfig
      | MicrosoftSharedVoicemailGroupsFieldConfig,
  ): MicrosoftTileState {
    this.microsoft360View = cloneDeep(ms360View);
    this.isResourceAccount = this.microsoft360View.accountType === AccountType.RESOURCE_ACCOUNT;

    switch (tile) {
      case MicrosoftTile.MS365: {
        return this._mapMs365ToTile(
          isConfigured,
          is365LicenseSyncInProgress,
          tileConfig.license,
          fieldConfig as Microsoft365LicensesFieldConfig,
        );
      }
      case MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING: {
        return this._mapMsTeamsAudioConferencingToTile(tileConfig.meeting);
      }
      case MicrosoftTile.MICROSOFT_TEAMS_CALLING: {
        return this._mapMsTeamsCallingToTile(tileConfig.calling);
      }
      case MicrosoftTile.DUBBER: {
        return this._mapDubberToTile(tileConfig.dubber);
      }
      case MicrosoftTile.SECURITY_GROUP_MEMBERSHIP: {
        return this._mapSecurityGroupMembershipToTile(
          isConfigured,
          tileConfig.groups,
          fieldConfig as MicrosoftSecurityGroupsFieldConfig,
        );
      }
      case MicrosoftTile.SHARED_VOICEMAIL_GROUPS: {
        return this._mapSharedVoicemailGroupsToTile(
          isConfigured,
          fieldConfig as MicrosoftSharedVoicemailGroupsFieldConfig,
        );
      }
      case MicrosoftTile.CALL_QUEUES: {
        return this._mapCallQueuesToTile(tileConfig.callQueues);
      }
      case MicrosoftTile.AUTO_ATTENDANTS: {
        return this._mapAutoAttendantsToTile(tileConfig.autoAttendants);
      }
      case MicrosoftTile.RESOURCE_ACCOUNTS: {
        return this._mapResourceAccountsToTile(tileConfig.resourceAccounts);
      }
      default:
        return null;
    }
  }

  private _mapResourceAccountsToTile(resourceAccountsTileStatus: TileStatus): MicrosoftTileState {
    return {
      ...this._defaultTileState,
      dataAutomation: 'resource-accounts-tile',
      serviceName: 'tkey;microsoft_360.resource_accounts.tile.title',
      description: this.microsoft360View.microsoft365UserLicenses?.userPrincipalName,
      serviceType: MicrosoftTile.RESOURCE_ACCOUNTS,
      tileType: ServiceTileType.EDIT,
      icon: ZiroIcons.PHONE_LIST,
      isEnabled: this.isResourceAccount,
      isDisplayed:
        resourceAccountsTileStatus === TileStatus.SHOW || resourceAccountsTileStatus === TileStatus.SHOW_WITH_DELETE,
    };
  }

  private _mapCallQueuesToTile(queuesTileStatus: TileStatus): MicrosoftTileState {
    return {
      ...this._defaultTileState,
      dataAutomation: 'call-queues-tile',
      serviceName: 'tkey;microsoft_360.call_queues.tile.title',
      description:
        this.microsoft360View.resourceAccountDetails &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.displayName,
      serviceType: MicrosoftTile.CALL_QUEUES,
      tileType:
        this.microsoft360View.resourceAccountDetails &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication
          ? ServiceTileType.EDIT
          : ServiceTileType.ADD,
      icon: ZiroIcons.CALL_QUEUE_OUTLINE,
      isEnabled: !!this.microsoft360View.resourceAccountDetails,
      isDisplayed: queuesTileStatus === TileStatus.SHOW || queuesTileStatus === TileStatus.SHOW_WITH_DELETE,
    };
  }

  private _mapAutoAttendantsToTile(autoAttendantTileStatus: TileStatus): MicrosoftTileState {
    return {
      ...this._defaultTileState,
      dataAutomation: 'auto-attendants-tile',
      serviceName: 'tkey;microsoft_360.auto_attendants.tile.title',
      description:
        this.microsoft360View.resourceAccountDetails &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication.displayName,
      serviceType: MicrosoftTile.AUTO_ATTENDANTS,
      tileType:
        this.microsoft360View.resourceAccountDetails &&
        this.microsoft360View.resourceAccountDetails.associatedVoiceApplication
          ? ServiceTileType.EDIT
          : ServiceTileType.ADD,
      icon: ZiroIcons.AUTO_ATTENDANT_OUTLINE,
      isEnabled:
        !!this.microsoft360View.resourceAccountDetails &&
        !!this.microsoft360View.resourceAccountDetails.associatedVoiceApplication,
      isDisplayed:
        autoAttendantTileStatus === TileStatus.SHOW || autoAttendantTileStatus === TileStatus.SHOW_WITH_DELETE,
      tooltip:
        !!this.microsoft360View.resourceAccountDetails &&
        !this.microsoft360View.resourceAccountDetails.associatedVoiceApplication
          ? 'tkey;global.coming_soon.text'
          : '',
    };
  }

  private _mapMsTeamsAudioConferencingToTile(meetingsTileStatus: TileStatus): MicrosoftTileState {
    const audioConferenceStatus = this.microsoft360View.licensingStatus.teamsAudioConferencingStatus;
    const isTileShown = meetingsTileStatus === TileStatus.SHOW || meetingsTileStatus === TileStatus.SHOW_WITH_DELETE;

    const tileType = isTileShown
      ? audioConferenceStatus === LicensingState.LICENSED
        ? ServiceTileType.EDIT
        : ServiceTileType.ADD
      : null;
    const toolTipToDisplay = this.isResourceAccount
      ? 'tkey;microsoft_360.view.teams.audio.conference.tile.resource.account.disabled'
      : audioConferenceStatus === LicensingState.UNLICENSED
        ? 'tkey;shared.model.ms_teams_audio_conferencing.disabled.tooltip'
        : audioConferenceStatus === LicensingState.LICENSING_IN_PROGRESS
          ? 'tkey;microsoft_360.view.teams.audio.conference.tile.refresh.tooltip'
          : '';

    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-ms-teams-audio-conferencing-tile',
      serviceName: 'tkey;microsoft_360.view.teams.audio.conference.tile.title',

      icon: this.ziroIcons.MEETINGS,
      serviceType: MicrosoftTile.MICROSOFT_TEAMS_AUDIO_CONFERENCING,
      tileType: tileType,
      description: this.microsoft360View.teamsAudioConferencing
        ? this._formatAudioConferencingNumber(this.microsoft360View.teamsAudioConferencing.audioConferenceNumber)
        : '',
      isEnabled: !this.isResourceAccount && audioConferenceStatus === LicensingState.LICENSED,
      tooltip: toolTipToDisplay,
      isDisplayed: isTileShown,
    };
  }

  private _mapMs365ToTile(
    licenseConfigured: boolean,
    isSyncInProgress: boolean,
    licenseTileStatus: TileStatus,
    fieldConfig: Microsoft365LicensesFieldConfig,
  ): MicrosoftTileState {
    const ms365User = this.microsoft360View?.microsoft365UserLicenses;
    const isTileShown = licenseTileStatus === TileStatus.SHOW || licenseTileStatus === TileStatus.SHOW_WITH_DELETE;
    const displayTooltip = isSyncInProgress
      ? 'tkey;userdetail.add.service_tile.microsoft.licenses_sync.tooltip'
      : !ms365User
        ? 'tkey;userdetail.add.service_tile.no_ms365_user.tooltip'
        : !licenseConfigured
          ? 'tkey;userdetail.add.service_tile.microsoft.no.licenses.tooltip'
          : null;
    const licenseIds = fieldConfig.readOnlyLicenseList
      .concat(fieldConfig.writeLicenseList)
      .map((group) => group.microsoft365LicenseJson.skuId);
    const hasLicense = !!this.microsoft360View.microsoft365UserLicenses.licenses.find(
      (license: AssignedMicrosoft365License) => licenseIds.includes(license.skuId),
    );

    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-ms-365-tile',
      serviceName: 'tkey;microsoft_360.view.licenses.tile.title',
      icon: this.ziroIcons.MICROSOFT_365_TILE,
      serviceType: MicrosoftTile.MS365,
      tileType: isTileShown ? (hasLicense && !isSyncInProgress ? ServiceTileType.EDIT : ServiceTileType.ADD) : null,
      description: ms365User ? ms365User.userPrincipalName : '',
      isEnabled: !!ms365User && licenseConfigured && !isSyncInProgress,
      tooltip: displayTooltip,
      isDisplayed: isTileShown,
    };
  }

  private _mapMsTeamsCallingToTile(teamsCallingTileStatus: TileStatus): MicrosoftTileState {
    const containsResourceLicense = this._containsResourceAccountLicense();
    const isUnlicensedResourceAccount = this.isResourceAccount && !containsResourceLicense;
    const sharedCallingLineUri =
      this.microsoft360View.teamsVoicePolicies?.sharedCallRoutingPolicy.resourceAccountLineUri;
    const msTeamsLine = this.microsoft360View.teamsPhoneNumberAssignment;
    const displayToolTip = isUnlicensedResourceAccount
      ? 'tkey;shared.model.microsoft_teams.tile.unlicensed_resource_account.tooltip'
      : this.microsoft360View.licensingStatus.teamsPhoneStatus === LicensingState.UNLICENSED
        ? 'tkey;shared.model.microsoft_teams.tile.no_license.tooltip'
        : this.microsoft360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSING_IN_PROGRESS
          ? 'tkey;microsoft_360.view.teams.calling.tile.refresh.tooltip'
          : '';
    const isTileShown =
      teamsCallingTileStatus === TileStatus.SHOW || teamsCallingTileStatus === TileStatus.SHOW_WITH_DELETE;
    const tileType = isTileShown
      ? msTeamsLine?.lineUri || sharedCallingLineUri
        ? ServiceTileType.EDIT
        : ServiceTileType.ADD
      : null;
    // Prioritize assigned number over shared calling - show assigned number if it exists
    const description = msTeamsLine?.lineUri
      ? msTeamsLine.lineUriExtension
        ? `${this.telephoneNumberFilter.transform(msTeamsLine.lineUri)} ext. ${msTeamsLine.lineUriExtension}`
        : `${this.telephoneNumberFilter.transform(msTeamsLine.lineUri)}`
      : sharedCallingLineUri
        ? `${this.telephoneNumberFilter.transform(sharedCallingLineUri)}`
        : '';
    const isImmediateCallForwarding = this.microsoft360View.immediateCallForwardEnabled;

    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-ms-teams-calling-tile',
      serviceName: msTeamsLine?.lineUri
        ? 'tkey;microsoft_360.view.teams.calling.tile.title'
        : sharedCallingLineUri
          ? 'tkey;microsoft_360.view.teams.calling.tile.shared_calling.title'
          : 'tkey;microsoft_360.view.teams.calling.tile.title',
      icon: this.ziroIcons.DESKPHONE_TILE,
      serviceType: MicrosoftTile.MICROSOFT_TEAMS_CALLING,
      tileType: tileType,
      description: description,
      isEnabled:
        (this.microsoft360View.licensingStatus.teamsPhoneStatus === LicensingState.LICENSED ||
          this.microsoft360View.licensingStatus.teamsPhoneStatus ===
            LicensingState.LICENSED_WITH_MICROSOFT_CALLING_PLAN) &&
        (!this.isResourceAccount || containsResourceLicense),
      tooltip: displayToolTip,
      isDisplayed: isTileShown,
      isImmediateCallForwarding: isImmediateCallForwarding,
      immediateCallForwardingTooltip: isImmediateCallForwarding
        ? 'tkey;shared.model.ms_teams_calling.tile.immediate_call_forwarding.tooltip'
        : '',
    };
  }

  private _mapSecurityGroupMembershipToTile(
    isConfigured: boolean,
    tileStatus: TileStatus,
    securityGroupFieldConfig?: MicrosoftSecurityGroupsFieldConfig,
  ): MicrosoftTileState {
    const readWriteGroupIds = securityGroupFieldConfig.readOnlySecurityGroupList
      .concat(securityGroupFieldConfig.writeSecurityGroupList)
      .map((group) => group.id);
    const hasMembership = !!this.microsoft360View.securityGroupMemberships.find((group: MicrosoftSecurityGroupOption) =>
      readWriteGroupIds.includes(group.id),
    );

    const ms365User = this.microsoft360View?.microsoft365UserLicenses;
    const displayToolTip = !ms365User
      ? 'tkey;userdetail.add.service_tile.no_ms365_user.tooltip'
      : !isConfigured
        ? 'tkey;microsoft_360.add.service_tile.microsoft.no.security.groups.tooltip'
        : null;
    const isTileShown = tileStatus === TileStatus.SHOW || tileStatus === TileStatus.SHOW_WITH_DELETE;
    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-ms-security-groups-tile',
      serviceName: 'tkey;microsoft_360.view.security.groups.tile.title',
      icon: this.ziroIcons.SECURITY_GROUPS_TILE,
      serviceType: MicrosoftTile.SECURITY_GROUP_MEMBERSHIP,
      tileType: hasMembership ? ServiceTileType.EDIT : ServiceTileType.ADD,
      description: ms365User ? ms365User.userPrincipalName : '',
      isEnabled: !!ms365User && isConfigured,
      tooltip: displayToolTip,
      isDisplayed: isTileShown,
    };
  }

  private _mapSharedVoicemailGroupsToTile(
    isConfigured: boolean,
    fieldConfig: MicrosoftSharedVoicemailGroupsFieldConfig,
  ): MicrosoftTileState {
    const ms365User = this.microsoft360View?.microsoft365UserLicenses;
    const readWriteGroupIds = fieldConfig
      ? fieldConfig.readOnlyGroupList.concat(fieldConfig.writeGroupList).map((group) => group.id)
      : [];
    const memberships = this.microsoft360View.sharedVoicemailGroupMemberships;
    const hasMembership = memberships
      ? memberships.some((group: MicrosoftSharedVoicemailGroupOption) => readWriteGroupIds.includes(group.id))
      : false;
    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-ms-shared-voicemail-groups-tile',
      serviceName: 'tkey;microsoft_360.view.shared_voicemail_groups.tile.title',
      icon: this.ziroIcons.SHARED_VOICEMAIL_GROUPS,
      serviceType: MicrosoftTile.SHARED_VOICEMAIL_GROUPS,
      tileType: hasMembership ? ServiceTileType.EDIT : ServiceTileType.ADD,
      description: ms365User ? ms365User.userPrincipalName : '',
      isEnabled: isConfigured,
      isDisplayed: isConfigured,
    };
  }

  private _mapDubberToTile(dubberTileStatus: TileStatus): MicrosoftTileState {
    const dubberUser = this.microsoft360View.dubberUser;
    const isTileShown = dubberTileStatus === TileStatus.SHOW || dubberTileStatus === TileStatus.SHOW_WITH_DELETE;
    return {
      ...this._defaultTileState,
      dataAutomation: 'microsoft-360-dubber-tile',
      serviceName: 'tkey;microsoft_360.view.dubber.tile.title',
      icon: this.ziroIcons.DUBBER_LOGO,
      serviceType: MicrosoftTile.DUBBER,
      tileType: dubberUser ? ServiceTileType.EDIT : ServiceTileType.ADD,
      isEnabled: isTileShown,
      isDisplayed: isTileShown,
    };
  }

  private _containsResourceAccountLicense(): boolean {
    return this.microsoft360View.microsoft365UserLicenses.licenses.some((license) =>
      license.associatedServicePlans.some((servicePlan) => servicePlan.startsWith('MCOEV_VIRTUALUSER')),
    );
  }

  private _formatAudioConferencingNumber(audioConferencingDescription: string): string {
    const audioConfNumber = audioConferencingDescription.split(' ');
    const phoneNumber = /\d/.test(audioConfNumber[1])
      ? `${audioConfNumber[0]} ${audioConfNumber[1]}`
      : audioConfNumber[0];
    return this.telephoneNumberFilter.transform(phoneNumber);
  }
}
